// pages/WebsiteLinks/EditTrainedData.js
import React, { useState, useEffect, useCallback } from "react";
import axiosInstance from "../../../utils/axiosInstance";

import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Table from '@tiptap/extension-table';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import TableRow from '@tiptap/extension-table-row';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
// <<< CHANGE: Image extension is NOT imported here
import { Button, Modal, Input, Space, Select, Dropdown, Menu } from 'antd';
import { DownOutlined } from '@ant-design/icons';
import { marked } from 'marked'; 

const { Option } = Select;

// Helper function to ensure URL has a protocol
const ensureUrlProtocol = (url) => {
  if (!url) return '';
  if (!/^https?:\/\//i.test(url) && !/^mailto:/i.test(url) && !/^tel:/i.test(url)) {
    return 'https://' + url;
  }
  return url;
};

export default function EditTrainedData({ document, onClose, onUpdate, chatbotId, permission  }) {
  const [pageContent, setPageContent] = useState("<p></p>");
  
  const [error, setError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isTableDialogOpen, setIsTableDialogOpen] = useState(false);
  const [tableRows, setTableRows] = useState(3);
  const [tableColumns, setTableColumns] = useState(3);
  const [toolbarSaveStatus, setToolbarSaveStatus] = useState('save');

  const editor = useEditor({
    extensions: [
      StarterKit.configure({}),
      Underline,
      Table.configure({ resizable: true }),
      TableCell, TableHeader, TableRow,
      Link.configure({
        openOnClick: false,
        autolink: true,
        linkOnPaste: true,
        HTMLAttributes: {
          target: '_blank',
          rel: 'noopener noreferrer',
        },
      }),
      // <<< CHANGE: Image extension is REMOVED from this array
    ],
    content: pageContent,
    onUpdate: ({ editor }) => setPageContent(editor.getHTML()),
    editorProps: { attributes: { class: 'tiptap-editor-area' } },
  });

  useEffect(() => {
    if (document) {
      // Tiptap will automatically strip the <img> tags because the Image extension is not loaded.
      const pageContentAsHtml = document.page_content ? marked.parse(document.page_content, { gfm: true }) : "<p></p>";
      if (editor && editor.getHTML() !== pageContentAsHtml) {
          editor.commands.setContent(pageContentAsHtml, false);
      }
    }
  }, [document, editor]);

  // <<< START: NEW CODE (Requirement 1) >>>
  // Handle Ctrl/Cmd + click on links to open in a new tab
  useEffect(() => {
    if (!editor || !editor.view.dom) return;

    const editorElement = editor.view.dom;
    const handleLinkClick = (event) => {
        const link = event.target.closest('a');
        if (link && (event.ctrlKey || event.metaKey)) {
            event.preventDefault();
            window.open(link.href, '_blank', 'noopener, noreferrer');
        }
    };

    editorElement.addEventListener('click', handleLinkClick);

    return () => {
        editorElement.removeEventListener('click', handleLinkClick);
    };
  }, [editor]);
  // <<< END: NEW CODE >>>


  const setLink = useCallback(() => {
    if (!editor) return;
    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt('Enter URL:', previousUrl || '');
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    const validatedUrl = ensureUrlProtocol(url);
    editor.chain().focus().extendMarkRange('link').setLink({ href: validatedUrl }).run();
  }, [editor]);

  // <<< CHANGE: The addImage function is removed as it's not needed.

  const handleSave = async () => {
    if (!editor) {
        setError("Editor is not ready.");
        return;
    }
    setIsSaving(true);
    setToolbarSaveStatus('saving');
    setError(null);

    try {
      const contentToSave = editor.getHTML(); 
      const params = chatbotId ? { chatbot_id: chatbotId } : {};

      const payload = { 
        id: document.id, 
        page_content: contentToSave,
        summary: document.summary,
        all_data: document
      };

      const response = await axiosInstance.post(
        `/update-trained-data`,
          payload, {
            params
          }
      );

      if (response.status === 200) {
        onUpdate();
        setToolbarSaveStatus('saved');
        setTimeout(() => setToolbarSaveStatus('save'), 1500);
      } else {
         setError(`Failed to update data (Status: ${response.status})`);
         setToolbarSaveStatus('save');
      }
    } catch (err) {
      const message = err.response?.data?.message || "Failed to update content.";
      setError(message);
      setToolbarSaveStatus('save');
      console.error("Error updating data:", err);
    } finally {
      setIsSaving(false);
    }
  };

  const insertTableDialog = useCallback(() => setIsTableDialogOpen(true), []);
  const handleInsertTable = useCallback(() => {
    if (!editor) return;
    const rows = parseInt(tableRows, 10);
    const cols = parseInt(tableColumns, 10);
    if (rows > 0 && cols > 0) {
        editor.chain().focus().insertTable({ rows, cols, withHeaderRow: true }).run();
    }
    setIsTableDialogOpen(false);
  }, [editor, tableRows, tableColumns]);
  const closeTableDialog = useCallback(() => setIsTableDialogOpen(false), []);
  const handleTableRowsChange = useCallback((e) => setTableRows(Math.max(1, parseInt(e.target.value, 10) || 1)), []);
  const handleTableColumnsChange = useCallback((e) => setTableColumns(Math.max(1, parseInt(e.target.value, 10) || 1)), []);

  const handleHeadingChange = useCallback((value) => {
    if (!editor) return;
    if (value === 'paragraph') {
        editor.chain().focus().setParagraph().run();
    } else {
        const level = parseInt(value.slice(1), 10);
        if (level >= 1 && level <= 6) {
           editor.chain().focus().toggleHeading({ level }).run();
        }
    }
  }, [editor]);
  
  const tableMenu = (
    <Menu>
      <Menu.Item key="insertTable" onClick={insertTableDialog}>Insert Table</Menu.Item>
      <Menu.Item key="addColumn" onClick={() => editor?.chain().focus().addColumnAfter().run()} disabled={!editor?.can().addColumnAfter()}>Add Column After</Menu.Item>
      <Menu.Item key="addRow" onClick={() => editor?.chain().focus().addRowAfter().run()} disabled={!editor?.can().addRowAfter()}>Add Row After</Menu.Item>
      <Menu.Item key="deleteColumn" onClick={() => editor?.chain().focus().deleteColumn().run()} disabled={!editor?.can().deleteColumn()}>Delete Column</Menu.Item>
      <Menu.Item key="deleteRow" onClick={() => editor?.chain().focus().deleteRow().run()} disabled={!editor?.can().deleteRow()}>Delete Row</Menu.Item>
      <Menu.Divider />
      <Menu.Item key="deleteTable" onClick={() => editor?.chain().focus().deleteTable().run()} disabled={!editor?.can().deleteTable()}>Delete Table</Menu.Item>
    </Menu>
  );

  const currentHeadingLevel = () => {
    if (!editor) return 'paragraph';
    for (let i = 1; i <= 6; i++) {
      if (editor.isActive('heading', { level: i })) return `h${i}`;
    }
    return 'paragraph';
  };

  if (!editor) {
    return <div className="text-center p-5"><i className="fa fa-spinner fa-spin fa-2x"></i> Loading Editor...</div>;
  }

  return (
    <div className="edit-trained-data" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{ paddingBottom: '10px', borderBottom: '1px solid #eee', marginBottom: '10px', flexShrink: 0 }}>
        <h5 style={{ margin: 0, fontSize: '0.95rem', wordBreak: 'break-word' }}>Edit Page Content for: {document?.website_link}</h5>
      </div>

      <div className="sticky-toolbar" style={{ flexShrink: 0, marginBottom: '10px', border: '1px solid #ddd', padding: '5px', borderRadius: '4px', background: '#f8f9fa' }}>
        <div className="toolbar" style={{ display: 'flex', flexWrap: 'wrap', gap: '5px', alignItems: 'center' }}>
          <button onClick={() => editor?.chain().focus().toggleBold().run()} className={editor?.isActive('bold') ? 'is-active' : ''} title="Bold"><i className="fa fa-bold"></i></button>
          <button onClick={() => editor?.chain().focus().toggleItalic().run()} className={editor?.isActive('italic') ? 'is-active' : ''} title="Italic"><i className="fa fa-italic"></i></button>
          <button onClick={() => editor?.chain().focus().toggleUnderline().run()} className={editor?.isActive('underline') ? 'is-active' : ''} title="Underline"><i className="fa fa-underline"></i></button>
          <button onClick={() => editor?.chain().focus().toggleStrike().run()} className={editor?.isActive('strike') ? 'is-active' : ''} title="Strike"><i className="fa fa-strikethrough"></i></button>
          <button onClick={setLink} className={editor?.isActive('link') ? 'is-active' : ''} title="Link"><i className="fa fa-link"></i></button>
          {/* <<< CHANGE: Image button is REMOVED from the toolbar */}
          <Select value={currentHeadingLevel()} style={{ width: 120 }} onChange={handleHeadingChange}>
            <Option value="paragraph">Paragraph</Option>
            {[1, 2, 3, 4, 5, 6].map(level => <Option key={level} value={`h${level}`}>Heading {level}</Option>)}
          </Select>
          <button onClick={() => editor?.chain().focus().toggleBulletList().run()} className={editor?.isActive('bulletList') ? 'is-active' : ''} title="Bullet List"><i className="fa fa-list-ul"></i></button>
          <button onClick={() => editor?.chain().focus().toggleOrderedList().run()} className={editor?.isActive('orderedList') ? 'is-active' : ''} title="Ordered List"><i className="fa fa-list-ol"></i></button>
          <Dropdown overlay={tableMenu} trigger={['click']} overlayStyle={{ zIndex: 1500 }}>
            <Button style={{ marginLeft: '8px', marginRight: '8px' }}><i className="fa fa-table" style={{ marginRight: '4px'}}></i> Table <DownOutlined /></Button>
          </Dropdown>
          <button onClick={() => editor?.chain().focus().undo().run()} disabled={!editor?.can().undo()} title="Undo"><i className="fa fa-undo"></i></button>
          <button onClick={() => editor?.chain().focus().redo().run()} disabled={!editor?.can().redo()} title="Redo"><i className="fa fa-repeat"></i></button>
          {permission.canModify && (
            <button onClick={handleSave} title="Save Changes">
              {toolbarSaveStatus === 'saving' ? <i className="fa fa-spinner fa-spin"></i> : toolbarSaveStatus === 'saved' ? <i className="fa fa-check"></i> : <i className="fa fa-save"></i>}
            </button>
          )}
        </div>
      </div>
      
      <div className="editor-container" style={{ flexGrow: 1, overflow: 'hidden', marginBottom: '15px' }}>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', border: '1px solid #ced4da', borderRadius: '0.25rem' }}>
            <label htmlFor="content-editor" style={{ fontWeight: 'bold', margin: '5px 0 5px 10px', display: 'block', flexShrink: 0 }}>Page Content</label>
            <div style={{ flexGrow: 1, overflowY: 'auto' }}>
                <EditorContent editor={editor}/>
            </div>
        </div>
      </div>

      <div style={{ marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: '1px solid #eee', flexShrink: 0, display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
        <div style={{ flex: '1 1 100%', minWidth: 0 }}>
          {error && <div className="alert alert-danger" style={{ margin: 0, padding: '0.5rem 1rem', fontSize: '0.8rem', wordBreak: 'break-word' }}>{error}</div>}
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {permission.canModify && (
            <Button type="primary" onClick={handleSave} loading={isSaving} icon={<i className="fa fa-save" style={{ marginRight: '4px' }}></i>} style={{ marginRight: '0' }}>
              {isSaving ? 'Saving...' : 'Save Content'}
            </Button>
          )}
          <Button onClick={onClose} disabled={isSaving} icon={<i className="fa fa-times" style={{ marginRight: '4px' }}></i>}>
             Cancel
          </Button>
        </div>
      </div>

      <Modal title="Insert Table" open={isTableDialogOpen} onOk={handleInsertTable} onCancel={closeTableDialog} okText="Insert" cancelText="Cancel" zIndex={1500}>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Input type="number" addonBefore="Rows" value={tableRows} onChange={handleTableRowsChange} min={1} />
          <Input type="number" addonBefore="Columns" value={tableColumns} onChange={handleTableColumnsChange} min={1} />
        </Space>
      </Modal>
      
      <style jsx global>{`
            .tiptap-editor-area { min-height: 200px; max-height: calc(85vh - 350px); overflow-y: auto; padding: 0.5rem; }
            @media (min-width: 600px) { .tiptap-editor-area { min-height: 400px; } }
            .ProseMirror { outline: none; }
            .ProseMirror p { margin-bottom: 0.5rem; line-height: 1.5; }
            .ProseMirror ul, .ProseMirror ol { padding-left: 1.5rem; margin-bottom: 0.5rem; }
            .ProseMirror li { margin-bottom: 0.1rem; }
            .ProseMirror li p { margin-bottom: 0.1rem; }
            .ProseMirror a { color: #0d6efd; text-decoration: underline; cursor: pointer; }
            .ProseMirror h1, .ProseMirror h2, .ProseMirror h3, .ProseMirror h4, .ProseMirror h5, .ProseMirror h6 { margin-top: 0.75rem; margin-bottom: 0.3rem; line-height: 1.3; font-weight: 500; }
            .ProseMirror table { border-collapse: collapse; margin: 0.5rem 0; width: 100%; }
            .ProseMirror th, .ProseMirror td { border: 1px solid #dee2e6; padding: 0.3rem 0.4rem; vertical-align: top; }
            .ProseMirror th { font-weight: bold; text-align: left; background-color: #f1f3f5; }
        `}</style>
    </div>
  );
}